import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { CarrierProvidersNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { BoxCarriers, CarrierPackages } from '@pdqdev/pdq-shared/dist/entities';
import { SettingsEditPackageService } from '../edit-package.service';
import * as _ from 'lodash';
import { IBoxRules, IBoxSettingsData } from '@pdqdev/pdq-shared/dist/interfaces';
import { validateBoxRulesData } from '@pdqdev/pdq-shared/dist/utils';
import { BoxService } from '@app/services/boxes/box.service';
import { Router } from '@angular/router';
import { CanDeactivateEditPackageGuard } from '../edit-package-guard/deactivate-edit-package-gurad';
import { ShopService } from '@app/services/shops/shop.service';

@Component({
  selector: 'app-settings-edit-carrier-package',
  templateUrl: './edit-carrier-package.component.html',
  styleUrls: ['./edit-carrier-package.component.scss', '../../../settings.scss'],
  standalone: false,
})
export class SettingsEditCarrierPackageComponent implements OnInit, OnChanges {
  @Input() boxData: IBoxSettingsData;
  @Input() boxesData: IBoxSettingsData[];
  @Output() scrollToBottom = new EventEmitter();
  @Input() unavailableKeyBinds = [];

  carrierData: CarrierData[];
  carriersTypeData;
  selectedCarrierPackage: IBoxSettingsData;
  validateBoxRulesDataError;
  isLoading = false;

  constructor(
    public settingsEditPackageService: SettingsEditPackageService,
    private formBuilder: UntypedFormBuilder,
    private boxService: BoxService,
    private router: Router,
    private canDeactivateEditPackageGuard: CanDeactivateEditPackageGuard,
    private shopService: ShopService,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.boxData || changes.boxesData) {
      this.initState();
    }
  }

  ngOnInit(): void {
    this.shopService.getShopCarriers().subscribe((carriers) => {
      const carrierData = _.groupBy(CarrierPackages, 'carrierType');
      this.settingsEditPackageService.showFormErrors = false;
      this.carrierData = Object.keys(carrierData).map((key: BoxCarriers) => {
        return {
          name: CarrierProvidersNames[key.toUpperCase()],
          icon: `/assets/icons/carriers-logos/${this.getIconByCarrierType(key)}`,
          open: false,
          packages: carrierData[key].filter(
            (p) => !p.carrierDependent || carriers?.find((c) => c.type?.name === p.carrierDependent),
          ),
          type: key,
        };
      });
      this.initState();
    });
  }

  initState() {
    let name;
    if (this.boxData) {
      this.carrierData.find((c) =>
        c.packages.find((p) => {
          if (p.token === this.boxData.token) {
            c.open = true;
            this.selectedCarrierPackage = p;
            return true;
          }
        }),
      );
      if (this.boxData.name !== this.selectedCarrierPackage.name) {
        name = this.boxData.name;
      }
    }
    this.initForm(name);
  }

  initForm(name?: string) {
    this.settingsEditPackageService.formGroup = this.formBuilder.group({
      name: [name ?? ''],
      condition: ['all'],
      rulesArray: this.formBuilder.array([]),
      keybind: [this.boxData?.keybind || ''],
    });
  }

  selectPackage(carrierPackage) {
    if (!this.boxData) {
      this.selectedCarrierPackage = carrierPackage;
    }
    this.scrollToBottom.emit();
  }

  getIconByCarrierType(carrierType: BoxCarriers) {
    switch (carrierType) {
      case BoxCarriers.FEDEX: {
        return 'fedex.png';
      }
      case BoxCarriers.UPS: {
        return 'ups.png';
      }
      case BoxCarriers.USPS: {
        return 'usps.png';
      }
    }
  }

  toggleDisplayCarrierData(carrier) {
    carrier.open = !carrier.open;
  }

  resetForm() {
    this.initForm();
    if (!this.boxData) {
      this.selectedCarrierPackage = null;
    } else {
      if (this.boxData?.name !== this.selectedCarrierPackage.name) {
        this.settingsEditPackageService.formGroup.controls.name.setValue(this.boxData.name);
      }
      this.settingsEditPackageService.resetConditions.next(true);
    }
  }

  onSubmit() {
    this.isLoading = true;
    this.validateBoxRulesDataError = null;
    if (this.settingsEditPackageService.formGroup.valid) {
      const rules = this.settingsEditPackageService.getRulesData();
      try {
        validateBoxRulesData(rules);
        if (this.boxData?.id) {
          const formValue = this.settingsEditPackageService.formGroup.value;
          const name = formValue?.name?.length ? formValue?.name : this.selectedCarrierPackage.name;
          this.boxService
            .updateBox({
              ...this.selectedCarrierPackage,
              ...formValue,
              name,
              rules,
              priority: this.boxData.priority,
              isActive: this.boxData.isActive,
              id: this.boxData.id,
            })
            .subscribe(() => {
              this.shopService.refreshBoxes();
              this.packageSaved();
            });
        } else {
          if (this.boxesData) {
            this.createBox(rules);
          } else {
            this.boxService.getBoxes({ excludeCustomBoxes: true }).subscribe((data) => {
              this.boxesData = data;
              this.createBox(rules);
            });
          }
        }
      } catch (e) {
        this.isLoading = false;
        this.validateBoxRulesDataError = `Condition error: ${e.message}`;
      }
    } else {
      this.isLoading = false;
      this.settingsEditPackageService.showFormErrors = true;
    }
  }

  createBox(rules: IBoxRules) {
    const maxPriority = this.boxesData?.length
      ? Math.max.apply(
          Math,
          this.boxesData.map(function (box) {
            return box.priority;
          }),
        )
      : 0;
    const formValue = this.settingsEditPackageService.formGroup.value;
    const name = formValue?.name?.length ? formValue?.name : this.selectedCarrierPackage.name;
    this.boxService
      .createBox({
        ...this.selectedCarrierPackage,
        id: undefined,
        ...formValue,
        name,
        rules,
        priority: maxPriority ? maxPriority + 1 : 1,
        isActive: true,
      })
      .subscribe(() => {
        this.shopService.refreshBoxes();
        this.packageSaved();
      });
  }

  packageSaved() {
    this.isLoading = false;
    this.router.navigate(['./settings/packages']);
    this.canDeactivateEditPackageGuard.submitted = true;
  }
}

export interface CarrierData {
  name: CarrierProvidersNames;
  icon: string;
  open: boolean;
  type: BoxCarriers;
  packages: IBoxSettingsData[];
}
